import type { EarTrainingMelody } from '../types/earTraining';
import { getClipSolfegeAnswer, SOLFEGE_OBSCURE } from './earTrainingSolfegeAnswer';
import { tonicToPitchClass } from './pitchLabel';

/** Semitone steps from tonic (natural major / natural minor). */
const MAJOR_SCALE_STEPS = [0, 2, 4, 5, 7, 9, 11] as const;
const MINOR_SCALE_STEPS = [0, 2, 3, 5, 7, 8, 10] as const;

/** True when every note pitch class lies in the melody key (no chromatic accidentals). */
export function melodyUsesOnlyDiatonicScaleDegrees(melody: EarTrainingMelody): boolean {
  const tonicPc = tonicToPitchClass(melody.tonic);
  const steps = melody.mode === 'major' ? MAJOR_SCALE_STEPS : MINOR_SCALE_STEPS;
  const allowed = new Set(steps.map(s => (tonicPc + s) % 12));
  return melody.notes.every(n => allowed.has(((n.midi % 12) + 12) % 12));
}

/** Interval between consecutive notes in semitones (absolute). */
export function intervalSemitones(a: number, b: number): number {
  return Math.abs(b - a);
}

export type MelodyDifficultyMetrics = {
  noteCount: number;
  stepCount: number;
  leapCount: number;
  leapRatio: number;
  maxLeap: number;
  directionChanges: number;
  rangeSemitones: number;
  rhythmVariety: number;
  chromaticSyllableCount: number;
  isMinor: boolean;
  fastTempo: boolean;
};

/**
 * Analyze a stored melody (transpose 0) for dictation-difficulty factors.
 * Based on common melodic-dictation research: leaps, contour changes, length,
 * range, rhythm variety, chromatic solfege, mode, tempo.
 */
export function analyzeMelodyDifficulty(melody: EarTrainingMelody): MelodyDifficultyMetrics {
  const notes = melody.notes;
  const midis = notes.map(n => n.midi);
  const noteCount = notes.length;

  let stepCount = 0;
  let leapCount = 0;
  let maxLeap = 0;
  let directionChanges = 0;
  let prevSign = 0;

  for (let i = 1; i < midis.length; i++) {
    const diff = midis[i]! - midis[i - 1]!;
    const size = Math.abs(diff);
    if (size <= 2) stepCount++;
    else leapCount++;
    maxLeap = Math.max(maxLeap, size);

    const sign = diff === 0 ? 0 : diff > 0 ? 1 : -1;
    if (sign !== 0 && prevSign !== 0 && sign !== prevSign) directionChanges++;
    if (sign !== 0) prevSign = sign;
  }

  const intervals = Math.max(1, noteCount - 1);
  const leapRatio = leapCount / intervals;

  const rangeSemitones =
    noteCount > 0 ? Math.max(...midis) - Math.min(...midis) : 0;

  const rhythmVariety = new Set(notes.map(n => n.beats)).size;

  const answer = getClipSolfegeAnswer({ ...melody, transposeSemitones: 0 });
  const chromaticSyllableCount = answer.filter(s => SOLFEGE_OBSCURE.has(s)).length;

  return {
    noteCount,
    stepCount,
    leapCount,
    leapRatio,
    maxLeap,
    directionChanges,
    rangeSemitones,
    rhythmVariety,
    chromaticSyllableCount,
    isMinor: melody.mode === 'minor',
    fastTempo: melody.tempoBpm >= 96,
  };
}

/**
 * Raw difficulty score (roughly 0–100). Higher = harder.
 * Weights reflect dictation literature: leaps and contour dominate, then length/range.
 */
export function rawDifficultyScore(m: MelodyDifficultyMetrics): number {
  let score = 0;

  // Leaps (strongest predictor in dictation studies)
  score += m.leapRatio * 38;
  score += Math.min(m.maxLeap, 12) * 2.8;
  score += m.leapCount * 4;

  // Contour instability (many direction changes vs one arch)
  score += m.directionChanges * 5.5;

  // Length and span
  score += Math.max(0, m.noteCount - 5) * 4.5;
  score += m.rangeSemitones * 1.4;

  // Rhythm complexity
  score += (m.rhythmVariety - 1) * 5;
  const hasDots = m.rhythmVariety > 1;
  if (hasDots) score += 4;

  // Tonality / chromatic solfege
  if (m.isMinor) score += 5;
  score += m.chromaticSyllableCount * 6;

  if (m.fastTempo) score += 3;

  return score;
}

/** Map raw score to a rough absolute rank (for single-melody estimates). */
export function rawScoreToDifficulty(raw: number): number {
  if (raw < 18) return 1;
  if (raw < 26) return 2;
  if (raw < 34) return 3;
  if (raw < 42) return 4;
  if (raw < 50) return 5;
  if (raw < 58) return 6;
  if (raw < 66) return 7;
  if (raw < 76) return 8;
  if (raw < 88) return 9;
  return 10;
}

export function estimateMelodyDifficulty(melody: EarTrainingMelody): number {
  return rawScoreToDifficulty(rawDifficultyScore(analyzeMelodyDifficulty(melody)));
}

/**
 * Assign ranks 1–10 across a melody bank: sort by raw score (leaps, contour, etc.),
 * then spread into deciles so each level has tunes as the bank grows.
 */
export function assignDifficultyRanks(melodies: EarTrainingMelody[]): EarTrainingMelody[] {
  const scored = melodies.map(melody => ({
    melody,
    raw: rawDifficultyScore(analyzeMelodyDifficulty(melody)),
  }));
  scored.sort((a, b) => a.raw - b.raw || a.melody.id.localeCompare(b.melody.id));
  const n = scored.length;
  if (n === 0) return [];
  if (n === 1) return [{ ...scored[0]!.melody, difficulty: 5 }];

  // Equal-sized buckets (~n/10 per level) so higher levels stay playable as the bank grows.
  const bucketSize = Math.max(1, Math.ceil(n / 10));
  return scored.map((s, i) => ({
    ...s.melody,
    difficulty: Math.min(10, Math.floor(i / bucketSize) + 1),
  }));
}

/** Human-readable factor summary for curation notes. */
export function formatDifficultyRationale(melody: EarTrainingMelody): string {
  const m = analyzeMelodyDifficulty(melody);
  const rank = estimateMelodyDifficulty(melody);
  const parts = [
    `rank ${rank}`,
    `${m.noteCount} notes`,
    `${m.leapCount} leaps (${Math.round(m.leapRatio * 100)}%)`,
    `max leap ${m.maxLeap} st`,
    `${m.directionChanges} contour turns`,
    `range ${m.rangeSemitones} st`,
  ];
  if (m.chromaticSyllableCount) parts.push(`${m.chromaticSyllableCount} chromatic syllables`);
  if (m.isMinor) parts.push('minor');
  return parts.join(', ');
}
